import { GiftDeliveryDatePolicy } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { DELIVERY_DATE_POLICY_DEFAULTS } from '@service/giftStore/utils/giftDeliveryDate.util';

/** 브랜드 폼의 배송 희망일 정책 입력값 (숫자는 입력 중 빈 문자열을 허용하려 문자열로 든다) */
export interface DeliveryDatePolicyForm {
  enabled: boolean;
  cutoffTime: string;
  leadDaysBeforeCutoff: string;
  leadDaysAfterCutoff: string;
  maxDaysAhead: string;
  closedWeekdays: number[];
  /** 'YYYY-MM-DD'를 줄바꿈·쉼표·공백으로 구분 */
  closedDates: string;
  arrivalNotice: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const CUTOFF_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const parseClosedDates = (text: string): string[] =>
  Array.from(new Set(text.split(/[\s,]+/).filter(Boolean))).sort();

/** 실제 달력에 있는 날짜인지 (2026-02-30 같은 값 거르기) */
const isRealDate = (date: string) => {
  if (!DATE_RE.test(date)) return false;
  const parsed = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
};

export const datePolicyToForm = (
  policy?: GiftDeliveryDatePolicy,
): DeliveryDatePolicyForm => ({
  enabled: policy?.enabled ?? false,
  cutoffTime: policy?.cutoffTime ?? DELIVERY_DATE_POLICY_DEFAULTS.cutoffTime,
  leadDaysBeforeCutoff: String(
    policy?.leadDaysBeforeCutoff ?? DELIVERY_DATE_POLICY_DEFAULTS.leadDaysBeforeCutoff,
  ),
  leadDaysAfterCutoff: String(
    policy?.leadDaysAfterCutoff ?? DELIVERY_DATE_POLICY_DEFAULTS.leadDaysAfterCutoff,
  ),
  maxDaysAhead: String(
    policy?.maxDaysAhead ?? DELIVERY_DATE_POLICY_DEFAULTS.maxDaysAhead,
  ),
  closedWeekdays: [...(policy?.closedWeekdays ?? [])],
  closedDates: (policy?.closedDates ?? []).join('\n'),
  arrivalNotice: policy?.arrivalNotice ?? '',
});

const toInt = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value) : NaN);

/** 저장 전 검증 — 문제 있으면 안내 문장, 없으면 null. 꺼져 있으면 검사하지 않는다 */
export const validateDatePolicyForm = (form: DeliveryDatePolicyForm): string | null => {
  if (!form.enabled) return null;
  if (!CUTOFF_RE.test(form.cutoffTime)) return '결제 마감시각을 입력해주세요.';
  const before = toInt(form.leadDaysBeforeCutoff);
  const after = toInt(form.leadDaysAfterCutoff);
  const max = toInt(form.maxDaysAhead);
  if (Number.isNaN(before) || Number.isNaN(after) || Number.isNaN(max)) {
    return '배송 희망일의 일수는 0 이상의 정수로 입력해주세요.';
  }
  if (after < before) {
    return '마감 후 가장 빠른 수령일은 마감 전보다 빠를 수 없습니다.';
  }
  if (max < after) {
    return '선택 가능 기간은 마감 후 가장 빠른 수령일보다 길어야 합니다.';
  }
  if (max > 60) return '선택 가능 기간은 60일 이내로 입력해주세요.';
  if (form.closedWeekdays.length >= 7) return '배송 불가 요일이 모든 요일입니다.';
  const invalid = parseClosedDates(form.closedDates).find((d) => !isRealDate(d));
  if (invalid) return `배송 불가일 형식이 올바르지 않습니다: ${invalid} (예: 2026-10-05)`;
  return null;
};

/**
 * 폼 → 저장값. 꺼져 있으면 기존 설정은 남기고 enabled만 내린다(다시 켤 때 값 유지),
 * 기존 값도 없으면 필드 자체를 만들지 않는다. existing을 spread해 모르는 필드를 보존한다.
 */
export function formToDatePolicy(
  form: DeliveryDatePolicyForm,
  existing?: GiftDeliveryDatePolicy,
): GiftDeliveryDatePolicy | undefined {
  if (!form.enabled) return existing ? { ...existing, enabled: false } : undefined;
  return {
    ...existing,
    enabled: true,
    cutoffTime: form.cutoffTime,
    leadDaysBeforeCutoff: Number(form.leadDaysBeforeCutoff),
    leadDaysAfterCutoff: Number(form.leadDaysAfterCutoff),
    maxDaysAhead: Number(form.maxDaysAhead),
    closedWeekdays: [...form.closedWeekdays].sort((a, b) => a - b),
    closedDates: parseClosedDates(form.closedDates),
    arrivalNotice: form.arrivalNotice.trim(),
  };
}
